import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ncbClient } from '../utils/ncbClient';
import { dropboxClient } from '../utils/dropboxClient';

const { FiPlay, FiCode, FiCheck, FiX } = FiIcons;

function ApiTestForm({ apiType }) {
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [customParams, setCustomParams] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const ncbEndpoints = [
    { id: 'create', label: 'Create User', method: 'POST', path: '/create/users' },
    { id: 'read', label: 'Get Users', method: 'GET', path: '/read/users' },
    { id: 'readById', label: 'Get User by ID', method: 'GET', path: '/read/users/{id}' },
    { id: 'search', label: 'Search Users', method: 'POST', path: '/search/users' },
    { id: 'update', label: 'Update User', method: 'PUT', path: '/update/users/{id}' },
    { id: 'delete', label: 'Delete User', method: 'DELETE', path: '/delete/users/{id}' }
  ];

  const dropboxEndpoints = [
    { id: 'listFiles', label: 'List Files', method: 'GET', path: '/files/list_folder' },
    { id: 'uploadFile', label: 'Upload File', method: 'POST', path: '/files/upload' },
    { id: 'downloadFile', label: 'Download File', method: 'GET', path: '/files/download' },
    { id: 'refreshToken', label: 'Refresh Token', method: 'POST', path: '/oauth2/token' }
  ];

  const endpoints = apiType === 'ncb' ? ncbEndpoints : dropboxEndpoints;

  const getExampleBody = (endpointId) => {
    if (apiType === 'ncb') {
      switch (endpointId) {
        case 'create':
        case 'search':
          return JSON.stringify({ username: 'testuser', password: 'testpass123' }, null, 2);
        case 'update':
          return JSON.stringify({ username: 'updateduser', password: 'newpass123' }, null, 2);
        default:
          return '';
      }
    } else {
      switch (endpointId) {
        case 'listFiles':
          return JSON.stringify({ path: '', recursive: false }, null, 2);
        case 'uploadFile':
          return JSON.stringify({ path: '/test-file.txt', mode: 'add', autorename: true }, null, 2);
        case 'refreshToken':
          return JSON.stringify({ refreshToken: 'your-refresh-token' }, null, 2);
        default:
          return '';
      }
    }
  };

  const handleEndpointChange = (endpointId) => {
    setSelectedEndpoint(endpointId);
    setRequestBody(getExampleBody(endpointId));
  };

  const handleUseExample = () => {
    if (selectedEndpoint) {
      setRequestBody(getExampleBody(selectedEndpoint));
    }
  };

  const handleClear = () => {
    setRequestBody('');
    setCustomParams('');
  };

  const handleExecuteRequest = async () => {
    if (!selectedEndpoint) return;

    setIsLoading(true);
    try {
      const endpoint = endpoints.find(e => e.id === selectedEndpoint);
      
      if (apiType === 'ncb') {
        let body = null;
        let params = {};
        
        if (requestBody.trim()) {
          body = JSON.parse(requestBody);
        }
        
        if (customParams.trim()) {
          params = JSON.parse(customParams);
        }
        
        await ncbClient.request(endpoint.method, endpoint.path, body, params);
      } else {
        let body = null;
        
        if (requestBody.trim()) {
          body = JSON.parse(requestBody);
        }
        
        await dropboxClient.request(endpoint.method, endpoint.path, body);
      }
    } catch (error) {
      console.error('Request failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateCurlCommand = () => {
    if (!selectedEndpoint) return '';

    const endpoint = endpoints.find(e => e.id === selectedEndpoint);
    const baseUrl = apiType === 'ncb' 
      ? (import.meta.env.VITE_NCB_BASE_URL || 'https://openapi.nocodebackend.com')
      : 'https://api.dropboxapi.com/2';

    let url = `${baseUrl}${endpoint.path}`;
    let headers = [];

    if (apiType === 'ncb') {
      const instanceId = import.meta.env.VITE_NCB_INSTANCE_ID || '53830_github_cloudflare_pages';
      url += `?Instance=${instanceId}`;
      headers.push('Authorization: Bearer YOUR_NCB_API_KEY');
    } else {
      headers.push('Authorization: Bearer YOUR_DROPBOX_ACCESS_TOKEN');
    }

    headers.push('Content-Type: application/json');

    let curl = `curl -X ${endpoint.method} "${url}"`;
    headers.forEach(header => {
      curl += ` -H "${header}"`;
    });

    if ((endpoint.method === 'POST' || endpoint.method === 'PUT') && requestBody.trim()) {
      const escapedBody = requestBody.replace(/'/g, "'\"'\"'");
      curl += ` -d '${escapedBody}'`;
    }

    return curl;
  };

  const handleCopyCurl = async () => {
    const curlCommand = generateCurlCommand();
    try {
      await navigator.clipboard.writeText(curlCommand);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getEnvStatus = (envVar) => {
    const value = import.meta.env[envVar];
    return value ? { status: 'set', text: '✓ Set', color: 'text-success-600' } : { status: 'missing', text: '✗ Missing', color: 'text-error-600' };
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {apiType === 'ncb' ? 'NoCodeBackend API Tester' : 'Dropbox API Tester'}
          </h2>
          <div className="flex items-center mt-1">
            <div className={`w-2 h-2 rounded-full mr-2 ${apiType === 'ncb' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
            <span className="text-sm text-gray-500">{apiType === 'ncb' ? 'NCB' : 'Dropbox'}</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-4">
        {/* Endpoint Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Endpoint</label>
          <select
            value={selectedEndpoint}
            onChange={(e) => handleEndpointChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Choose an endpoint...</option>
            {endpoints.map(endpoint => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.method} - {endpoint.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleUseExample}
            disabled={!selectedEndpoint}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use Example Body
          </button>
          <button
            onClick={handleClear}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Clear
          </button>
        </div>

        {/* Request Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Request Body (JSON)</label>
          <textarea
            value={requestBody}
            onChange={(e) => setRequestBody(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            placeholder="Enter JSON request body..."
          />
        </div>

        {/* Custom Parameters (NCB only) */}
        {apiType === 'ncb' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Query Parameters (JSON)</label>
            <textarea
              value={customParams}
              onChange={(e) => setCustomParams(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              placeholder='{"param1": "value1", "param2": "value2"}'
            />
          </div>
        )}

        {/* Action Area */}
        <div className="flex space-x-3 pt-4">
          <button
            onClick={handleExecuteRequest}
            disabled={!selectedEndpoint || isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <SafeIcon icon={FiPlay} className="h-4 w-4" />
            <span>{isLoading ? 'Running...' : 'Run Request'}</span>
          </button>
          
          <button
            onClick={handleCopyCurl}
            disabled={!selectedEndpoint}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={copySuccess ? FiCheck : FiCode} className="h-4 w-4" />
            <span>{copySuccess ? 'Copied!' : 'Copy cURL'}</span>
          </button>
        </div>

        {/* Environment Status */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Environment Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {apiType === 'ncb' ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span>NCB_BASE_URL</span>
                  <span className={getEnvStatus('VITE_NCB_BASE_URL').color}>
                    {getEnvStatus('VITE_NCB_BASE_URL').text}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>NCB_INSTANCE_ID</span>
                  <span className={getEnvStatus('VITE_NCB_INSTANCE_ID').color}>
                    {getEnvStatus('VITE_NCB_INSTANCE_ID').text}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>NCB_API_KEY</span>
                  <span className={getEnvStatus('VITE_NCB_API_KEY').color}>
                    {getEnvStatus('VITE_NCB_API_KEY').text}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span>DROPBOX_APP_KEY</span>
                  <span className={getEnvStatus('VITE_DROPBOX_APP_KEY').color}>
                    {getEnvStatus('VITE_DROPBOX_APP_KEY').text}
                  </span>
                </div>
                <div className="col-span-full text-xs text-gray-500 mt-2">
                  Note: Dropbox APP_SECRET and REFRESH_TOKEN must be set in Cloudflare Pages Functions, not in frontend code.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiTestForm;